import { useState, useSyncExternalStore, type MouseEvent } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { buildLocalizedPath } from "~/config/route-config";
import type { NavSessionUser } from "~/lib/nav-session";
import { ArrowLink } from "~/components/ui/ArrowLink";
import { Button } from "~/components/ui/Button";
import { Modal } from "~/components/ui/Modal";

/**
 * Whether leaving the auto-test now would lose something she typed or chose. The view
 * writes it (`setAutotestUnsaved`); the header's exit and its logo read it. A module store
 * because the header lives in the root, outside the view. Nothing is persisted: the answers
 * are health data and stay in memory (Codex's usability review, 2026-10-10, F4).
 */
let unsaved = false;
const listeners = new Set<() => void>();
export function setAutotestUnsaved(next: boolean) {
  if (unsaved === next) return;
  unsaved = next;
  listeners.forEach((fn) => fn());
}
const subscribe = (fn: () => void) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};
const read = () => unsaved;
/** For tests and non-React readers. */
export const isAutotestUnsaved = read;
const readServer = () => false;

/**
 * The auto-test's way out (F4, Gala 2026-10-10: `Si la de Astra te gusta, implementala`):
 * «Salir del test» on the right of the logo-only bar, to the public home. Before any answer
 * and from the result she leaves at once; with answers that would be lost, the exit — and
 * the logo, the bar's other way out — asks once. «Seguir con el test» keeps her exactly
 * where she was (the Modal returns the focus to the control she used). The browser's Back
 * and the test's own «Atrás» are untouched.
 */
export function useAutotestExit(sessionUser?: NavSessionUser | null) {
  const { t, i18n } = useTranslation("nav");
  const navigate = useNavigate();
  const pending = useSyncExternalStore(subscribe, read, readServer);
  const [asking, setAsking] = useState(false);
  const search = sessionUser ? "?public=1" : "";
  const home = `${buildLocalizedPath("home", i18n.language === "en" ? "en" : "es")}${search}`;

  /** For any link out of the test: lets it go, or holds it and asks. */
  const guard = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!pending || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    event.preventDefault();
    setAsking(true);
  };

  const control = (
    <ArrowLink to={home} className="appbar-exit" onClick={guard}>
      {t("funnel_exit.label")}
    </ArrowLink>
  );

  const dialog = (
    <Modal open={asking} onClose={() => setAsking(false)} ariaLabel={t("funnel_exit.title")} className="pub-confirm" showCloseButton={false}>
      <h2 className="pub-confirm-title">{t("funnel_exit.title")}</h2>
      <p className="pub-confirm-body">{t("funnel_exit.body")}</p>
      <div className="pub-confirm-actions">
        <Button type="button" variant="public-primary" onClick={() => setAsking(false)}>
          {t("funnel_exit.stay")}
        </Button>
        <Button
          type="button"
          variant="public-secondary"
          onClick={() => {
            setAsking(false);
            setAutotestUnsaved(false);
            navigate(home);
          }}
        >
          {t("funnel_exit.leave")}
        </Button>
      </div>
    </Modal>
  );

  return { guard, control, dialog };
}
